/**
 * Hızlı teklif "api istemci" katmanı: ekran sunucuyla YALNIZ buradan konuşur. Uçlar gerçektir
 * (`api/src/modules/hizli-teklif/`): fiyat işi, paket fiyatı, PDF, mesaj, teklif kaydı ve sürümler.
 * Ekran tipleri ile sunucu gövdeleri arasındaki çeviri `sozlesme.ts`te; bu dosya yalnız HTTP yapar.
 *
 * Uç bulunamazsa (501 ya da kodsuz 404) ekran "uç hazır değil" der; sessizce başarı sayılmaz. Gerçek bir "bulunamadı"
 * cevabı (`code` ya da `reason` taşır: iş süresi doldu, teklif yok) uç eksikliği sayılmaz.
 *
 * Testte bu uçlar `tests/e2e/support/hizli-teklif-sahte.ts` ile sahtelenir (tarayıcı düzeyinde, MSW benzeri);
 * sahte gövdeler sunucunun gerçek gövdeleridir.
 */
import { ApiError } from '../api';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import {
  kayitIstegiKur,
  mesajIstegiKur,
  pdfAdresiDogrula,
  pdfIstegiKur,
  surumKaydiCevir,
  surumOzetiCevir,
  type SunucuPdfCevabi,
  type SunucuSurumGorunumu,
} from './sozlesme';
import type {
  FiyatIsiDurumu,
  FiyatIsiIstegi,
  MesajCevabi,
  MesajTonu,
  PaketFiyati,
  PaketIstegi,
  SurumKaydi,
  SurumOzeti,
  TeklifSecimi,
  TeklifTaslagi,
} from './tipler';

const KOK = '/api/v1/hizli-teklif';

async function hataFirlat(response: Response): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as { message?: string | string[]; detail?: string; code?: string; correlationId?: string; reason?: string };
  const ileti = (Array.isArray(body.message) ? body.message.join('. ') : (body.message ?? body.detail ?? body.reason)) || M.baslatilamadi;
  throw new ApiError(response.status, ileti, body.code, body.correlationId, body.reason);
}

async function cagri(yol: string, signal: AbortSignal | undefined, veri?: unknown, yontem?: 'GET' | 'POST'): Promise<Response> {
  const method = yontem ?? (veri === undefined ? 'GET' : 'POST');
  const csrf = /(?:^|;\s*)mbd_csrf=([^;]+)/.exec(document.cookie)?.[1];
  const response = await fetch(`${KOK}${yol}`, {
    method,
    signal,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(method === 'POST' ? { 'x-csrf-token': csrf ? decodeURIComponent(csrf) : '' } : {}) },
    ...(veri === undefined ? {} : { body: JSON.stringify(veri) }),
  });
  if (!response.ok) return hataFirlat(response);
  return response;
}

/** Sürüm kaydı reddedilince gösterilecek cümle: kodlu cümle, yoksa 400/409 gövdesindeki sunucu mesajı. */
export const kayitHataCumlesi = (e: unknown): string | null => {
  const kodlu = sunucuCumlesi(e);
  if (kodlu !== null) return kodlu;
  return e instanceof ApiError && (e.status === 400 || e.status === 409) && e.message.trim() !== '' ? e.message : null;
};

/** Uç yoksa ekranın "hazır değil" diyebilmesi için ayrı sınıf: 501 ya da kodsuz 404 (rota yok). Gerçek 404'ler `code`/`reason` taşır. */
export const ucYok = (e: unknown): boolean => e instanceof ApiError && (e.status === 501 || (e.status === 404 && e.code === undefined && e.reason === undefined));

/** Satışçıya sunucunun kendi cümlesiyle gösterilecek çıktı hataları (servis ayarsız, fiyat süresi doldu ...). */
const SUNUCU_CUMLESI = new Set(['PDF_SERVISI_AYARSIZ', 'PDF_SERVISI_YOK', 'PDF_ZAMAN_ASIMI', 'FIYAT_SURESI_DOLDU', 'IS_BITMEDI', 'IS_HATALI', 'IS_YOK', 'OTEL_FIYATSIZ', 'GEZI_KATALOGDA_YOK', 'KAYIT_ACIK_TALEP_YOK', 'KAYIT_PARA_BIRIMI', 'KAYIT_TASLAK_BUYUK', 'KAYIT_PDF_YOK', 'KALEM_COK', 'KALEM_AD_YOK', 'KALEM_TUTAR_GECERSIZ', 'KALEM_MIKTAR_GECERSIZ', 'KALEM_ACIKLAMA_UZUN', 'KALEM_PARA_UYUMSUZ', 'KALEM_SECENEK_YOK', 'YAT_SECIMI_GECERSIZ', 'BOLUM_TARIH', 'BOLUM_ODA', 'OTEL_TEKRAR', 'PARA_FARKLI']);
export const sunucuCumlesi = (e: unknown): string | null => {
  if (!(e instanceof ApiError)) return null;
  const kod = e.reason ?? e.code;
  // Kayıt ucunun reddetme nedenleri (KAYIT_*) ve 400/409 gövdeleri satışçıya sunucunun Türkçe cümlesiyle gösterilir.
  return kod !== undefined && (SUNUCU_CUMLESI.has(kod) || kod.startsWith('KAYIT_')) ? e.message : null;
};

export interface PiyasaFiyatiOdasi {
  readonly yetiskin: number;
  readonly cocukYaslari?: readonly number[];
  /** Teklifte seçilen oda tipinin adı; yoksa o dolulukta en ucuz vergi dahil Booking odası. */
  readonly odaAdi?: string;
}

export interface PiyasaFiyatiIstegi {
  readonly slug: string;
  readonly girisTarihi: string;
  readonly cikisTarihi: string;
  readonly yetiskin: number;
  readonly cocukYaslari?: readonly number[];
  /** Çok odalı arama: oda başına doluluk. Verilmezse tek oda (yetiskin, cocukYaslari). */
  readonly odalar?: readonly PiyasaFiyatiOdasi[];
}

export interface PiyasaFiyatiOdaDokumu {
  readonly yetiskin: number;
  readonly cocukYaslari: readonly number[];
  readonly odaAdi: string;
  readonly tutarMinor: number;
}

export type PiyasaFiyatiCevabi =
  | { readonly durum: 'TAMAM'; readonly tutarMinor: number; readonly paraBirimi: string; readonly alinmaZamani: string; readonly odalar?: readonly PiyasaFiyatiOdaDokumu[] }
  | { readonly durum: 'BEKLIYOR' }
  | { readonly durum: 'YOK'; readonly sebep: string };

export interface KayitGirdisi {
  readonly secim: TeklifSecimi;
  readonly taslak: TeklifTaslagi;
  readonly mesaj: { readonly metin: string; readonly ton: MesajTonu } | null;
}

export interface HizliTeklifIstemcisi {
  baslat(istek: FiyatIsiIstegi, signal?: AbortSignal): Promise<{ id: string }>;
  durum(id: string, signal?: AbortSignal): Promise<FiyatIsiDurumu>;
  paketFiyatlari(istek: PaketIstegi, signal?: AbortSignal): Promise<{ paketler: readonly PaketFiyati[] }>;
  pdf(secim: TeklifSecimi, ton: MesajTonu, signal?: AbortSignal, dil?: 'tr' | 'en'): Promise<{ blob: Blob; dosyaAdi: string }>;
  mesaj(secim: TeklifSecimi, ton: MesajTonu, signal?: AbortSignal, dil?: 'tr' | 'en'): Promise<MesajCevabi>;
  surumKaydet(girdi: KayitGirdisi, signal?: AbortSignal): Promise<SurumOzeti>;
  surumler(musteriId: string, signal?: AbortSignal): Promise<{ surumler: readonly SurumOzeti[] }>;
  surum(teklifId: string, surumNo: number, signal?: AbortSignal): Promise<SurumKaydi>;
  /** Tek otel için Booking fiyatı (satışçı isteyince); BEKLIYOR dönerse aynı istek birkaç saniye sonra yenilenir. */
  piyasaFiyati(istek: PiyasaFiyatiIstegi, signal?: AbortSignal): Promise<PiyasaFiyatiCevabi>;
  /** Müşterinin talebindeki uyruk (varsayılan uyruk önerisi); yoksa null. */
  musteriUyruk(musteriId: string, signal?: AbortSignal): Promise<{ uyruk: string | null }>;
}

export const hizliTeklifApi: HizliTeklifIstemcisi = {
  baslat: async (istek, signal) => (await cagri('/fiyat-isleri', signal, istek)).json() as Promise<{ id: string }>,
  durum: async (id, signal) => (await cagri(`/fiyat-isleri/${encodeURIComponent(id)}`, signal)).json() as Promise<FiyatIsiDurumu>,
  piyasaFiyati: async (istek, signal) => (await cagri('/piyasa-fiyati', signal, istek)).json() as Promise<PiyasaFiyatiCevabi>,
  paketFiyatlari: async (istek, signal) => (await cagri('/paket-fiyatlari', signal, istek)).json() as Promise<{ paketler: readonly PaketFiyati[] }>,
  pdf: async (secim, ton, signal, dil = 'tr') => {
    const uretilen = (await (await cagri('/pdf', signal, pdfIstegiKur(secim, ton, dil))).json()) as SunucuPdfCevabi;
    // İndirme adresi sunucunun kendi PDF yolu olmak zorunda; `KOK` ön eki çağrıda eklenir.
    const yol = pdfAdresiDogrula(uretilen.indirmeAdresi).slice(KOK.length);
    const dosya = await cagri(yol, signal);
    return { blob: await dosya.blob(), dosyaAdi: /filename="([^"]+)"/.exec(dosya.headers.get('content-disposition') ?? '')?.[1] ?? uretilen.dosyaAdi };
  },
  mesaj: async (secim, ton, signal, dil = 'tr') => {
    const v = (await (await cagri('/mesaj', signal, mesajIstegiKur(secim, ton, dil))).json()) as { mesaj: string };
    return { metin: v.mesaj };
  },
  surumKaydet: async (girdi, signal) => {
    const g = (await (await cagri('/teklifler', signal, kayitIstegiKur(girdi.secim, girdi.taslak, girdi.mesaj, new Date()))).json()) as SunucuSurumGorunumu;
    return surumOzetiCevir(g);
  },
  surumler: async (musteriId, signal) => (await cagri(`/teklifler?musteriId=${encodeURIComponent(musteriId)}`, signal)).json() as Promise<{ surumler: readonly SurumOzeti[] }>,
  musteriUyruk: async (musteriId, signal) => (await cagri(`/teklifler/musteri-uyruk/${encodeURIComponent(musteriId)}`, signal)).json() as Promise<{ uyruk: string | null }>,
  surum: async (teklifId, surumNo, signal) => surumKaydiCevir((await (await cagri(`/teklifler/${encodeURIComponent(teklifId)}/surumler/${String(surumNo)}`, signal)).json()) as SunucuSurumGorunumu),
};
